import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, tap } from "rxjs";

import { environment } from "../../../../environments/environment";
import { AllDocumentType } from "../../../modules/document-templates/components/document-templates/models/all-document-type.model";
import { UploadFileContentDto } from "../../../modules/document-templates/components/document-templates/models/upload-file-content-dto.model";
import { DocumentForm } from "../../../modules/vendor/components/vendor-information/vendor-information/models/document-form.model";
import { VendorCompany } from "../../../shared/models/vendor-company.model";
import { VendorDocument } from "../../../shared/models/vendor-document.model";
import { VendorTemplate } from "../../../shared/models/vendor-template.model";
import { DeleteModuleDocument } from "../../models/deleteModuleDocument.model";
import { GenericResponseDto } from "../../models/genericResponseDto.model";
import { GetDocumentsSignatureStatusRequest } from "../../models/get-documents-signature-status-request.model";
import { VendorData } from "../../models/vendor-data.model";

@Injectable({
    providedIn: "root"
})
export class VendorDocumentService {
    constructor(private httpClient: HttpClient) {
    }

    httpOptions = {
        headers: new HttpHeaders({ "Content-Type": "application/json" })
    };

    private apiBaseUrl = `${environment.apiBaseUrl}/vendordocument`;
    document = new BehaviorSubject<any>(null);
    data: Observable<any> = this.document.asObservable();

    AddDocument(document: VendorDocument): Observable<GenericResponseDto<string>> {
        return this.httpClient.post<GenericResponseDto<string>>(
            `${this.apiBaseUrl}/add`,
            document,
            this.httpOptions
        );
    }

    DownloadDocuments(documents: any[]): Observable<any> {
        return this.httpClient.post(`${environment.apiBaseUrl}/Document/DownloadFiles`, documents, {
            ...this.httpOptions,
            responseType: "blob" // Set responseType to arraybuffer
        });
    }

    GetDocuments(vendorAccountNo: string, selectedYear?: number):
    Observable<GenericResponseDto<DocumentForm[]>> {
        const yearParam = selectedYear ? `&archiveYear=${selectedYear}` : "";
        return this.httpClient.get<GenericResponseDto<DocumentForm[]>>(
            `${this.apiBaseUrl}/all?vendorAccountNo=${vendorAccountNo}${yearParam}`,
            this.httpOptions
        ).pipe(
            tap((data) => this.document.next(data))
        );
    }

    GetDocumentsHistory(recordId: number): Observable<GenericResponseDto<[]>> {
        return this.httpClient.get<GenericResponseDto<[]>>(
            `${this.apiBaseUrl}/GetDocumentHistory/${recordId}`,
            this.httpOptions
        );
    }

    DownloadTemplate(templateId: number): Observable<GenericResponseDto<VendorTemplate>> {
        return this.httpClient.get<GenericResponseDto<VendorTemplate>>(
            `${this.apiBaseUrl}/template/download?templateId=${templateId}`,
            this.httpOptions
        );
    }

    AddDocumentInfo(company: VendorCompany): Observable<GenericResponseDto<string>> {
        return this.httpClient.post<GenericResponseDto<string>>(
            `${this.apiBaseUrl}/info/update`,
            company,
            this.httpOptions
        );
    }

    GetDocumentInfo(): Observable<GenericResponseDto<VendorCompany>> {
        return this.httpClient.get<GenericResponseDto<VendorCompany>>(
            `${this.apiBaseUrl}/info`,
            this.httpOptions
        );
    }

    GetAllDocumentTypes(): Observable<GenericResponseDto<AllDocumentType[]>> {
        return this.httpClient.get<GenericResponseDto<AllDocumentType[]>>(
            `${this.apiBaseUrl}/GetAllDocumentTypes`,
            this.httpOptions
        );
    }

    GetDocumentTypeById(id: string): Observable<GenericResponseDto<DocumentType>> {
        return this.httpClient.get<GenericResponseDto<DocumentType>>(
            `${this.apiBaseUrl}/GetDocumentTypeById?id=${id}`,
            this.httpOptions
        );
    }

    UpdateDocumentTypeById(id: string, data: UploadFileContentDto):
    Observable<GenericResponseDto<DocumentType>> {
        return this.httpClient.post<GenericResponseDto<DocumentType>>(
            `${this.apiBaseUrl}/UpdateDocumentTypeById?id=${id}`,
            data,
            this.httpOptions
        );
    }

    deleteVendorDocument(data: DeleteModuleDocument):
    Observable<GenericResponseDto<string>> {
        return this.httpClient.post<GenericResponseDto<string>>(
            `${this.apiBaseUrl}/DeleteVendorDocument`,
            data,
        );
    }

    FillAndSign(vendorAccountNo: string, archiveYear?: number):
    Observable<GenericResponseDto<any>> {
        const requestBody = {
            vendorAccountNo,
            archiveYear
        };
        return this.httpClient.post<GenericResponseDto<any>>(
            `${environment.apiBaseUrl}/DocuSign/FillAndSign`,
            requestBody,
            this.httpOptions
        );
    }

    GetMainContact(vendorAccountNumber: string, archiveYear?: number):
    Observable<GenericResponseDto<string>> {
        let url = `${environment.apiBaseUrl}/Vendor/maincontact?vendorAccountNumber=${encodeURIComponent(vendorAccountNumber)}`;
        if (archiveYear !== undefined) {
            url += `&archiveYear=${archiveYear}`;
        }
        return this.httpClient.get<GenericResponseDto<string>>(url, this.httpOptions);
    }

    CreateEnvelope(
        vendorAccountNo: string,
        recordId: number,
        signingFlow: string,
        archiveYear?: number,
        vendorEmail?: string
    ):
        Observable<GenericResponseDto<any>> {
        const requestBody: any = {
            vendorAccountNo,
            archiveYear
        };
        if (vendorEmail) {
            requestBody.Email = vendorEmail;
        }
        return this.httpClient.post<GenericResponseDto<any>>(
            `${environment.apiBaseUrl}/DocuSign/UploadSingleDoc?recordId=${recordId}&signingFlow=${signingFlow}`,
            requestBody,
            this.httpOptions
        );
    }

    /**
     * Get pending reviewer envelope IDs for a vendor account and year (GET)
     */
    GetPendingReviewerEnvelopeId(vendorAccountNo: string, archiveYear?: number): Observable<GenericResponseDto<any>> {
        let url = `${environment.apiBaseUrl}/DocuSign/PendingReviewerEnvelopeId?vendorAccountNumber=${encodeURIComponent(vendorAccountNo)}`;
        if (archiveYear !== undefined) {
            url += `&archiveYear=${archiveYear}`;
        }

        return this.httpClient.get<GenericResponseDto<string>>(url, this.httpOptions);
    }

    /**
     * Regenerate reviewer signing URL for a given envelope
     */
    RegenerateReviewerSigningUrl(envelopeId: string, vendorAccountNo: string): Observable<GenericResponseDto<any>> {
        const requestBody = {
            envelopeId,
            vendorAccountNumber: vendorAccountNo
        };
        return this.httpClient.post<GenericResponseDto<any>>(
            `${environment.apiBaseUrl}/DocuSign/RegenerateReviewerSigningUrl`,
            requestBody,
            this.httpOptions
        );
    }

    /**
     * Get documents signature status with pagination and filtering
     */
    GetDocumentsSignatureStatus(request: GetDocumentsSignatureStatusRequest):
    Observable<GenericResponseDto<VendorData[]>> {
        return this.httpClient.post<GenericResponseDto<VendorData[]>>(
            `${this.apiBaseUrl}/GetDocumentsSignatureStatus`,
            request,
            this.httpOptions
        );
    }

    /**
     * Export documents signature status to Excel
     */
    ExportDocumentsSignatureStatusToExcel(request: { SelectedAccountNumbers: string[]; ArchiveYear: number }):
    Observable<Blob> {
        return this.httpClient.post(`${this.apiBaseUrl}/ExportDocumentsSignatureStatusToExcel`, request, {
            ...this.httpOptions,
            responseType: "blob"
        }) as Observable<Blob>;
    }

    /**
     * Mark a document signature as signed
     */
    MarkSignature(documentId: string, signatureType: string, signatureValue: boolean):
    Observable<GenericResponseDto<string>> {
        const requestBody = {
            documentId,
            signatureType,
            signatureValue
        };
        return this.httpClient.post<GenericResponseDto<string>>(
            `${this.apiBaseUrl}/SignDocument`,
            requestBody,
            this.httpOptions
        );
    }

    /**
     * Create reviewer envelope for non-custom documents (documents without changes)
     * @param vendorDocumentRecordIds - Array of vendor document record IDs
     * @param archiveYear - The archive year
     */
    CreateReviewerEnvelopeByRecordId(
        vendorDocumentRecordIds: number[],
        archiveYear?: number
    ): Observable<GenericResponseDto<any>> {
        return this.httpClient.post<GenericResponseDto<any>>(
            `${environment.apiBaseUrl}/DocuSign/CreateReviewerEnvelopeByRecordId?archiveYear=${archiveYear}`,
            vendorDocumentRecordIds,
            this.httpOptions
        );
    }
}
